import localforage from 'localforage';
import { ownerForEnqueue, type QueueOwner } from './queueOwner';

const COMPOUND_QUEUE_KEY = 'rois_offline_compound_queue';
/** Clave de esta cola en localforage: el logout la conserva (spec 2026-10-12). */
export const COMPOUND_QUEUE_STORAGE_KEY = COMPOUND_QUEUE_KEY;
const MAX_RETRIES = 5;

export interface LocalImage {
  fileName: string;
  directory: string;
  base64Data: string;
  mimeType: string;
}

export interface CommunicationOfflineData {
  /** Mutation a ejecutar para crear la OT padre (opcional — Tab 0). */
  parentMutation?: 'SAVE_GENERIC_ORDER' | 'SAVE_CAMPAIGN';
  /** Input para la mutation padre (si aplica). */
  parentInput?: Record<string, unknown>;
  /** Input de la communication (siempre presente). */
  commInput: Record<string, unknown>;
}

export interface OfflineCompoundOperation {
  /**
   * Quién la encoló (spec 2026-10-01 § 6). Opcional: las encoladas antes de
   * esta versión no lo llevan y se envían con la sesión actual, como siempre.
   */
  owner?: QueueOwner;
  id: string;
  type: 'action' | 'action-edit' | 'accident' | 'accident-edit' | 'communication';
  timestamp: number;
  retryCount: number;
  status: 'pending' | 'syncing' | 'failed';
  parentOrderId: string;
  parentOrderRoid: string;
  isCampaignParent: boolean;
  sessionSnapshots: Record<string, string | null>;
  localImages: LocalImage[];
  errorMessage?: string;

  /**
   * Para `type: 'communication'`. Inputs ya construidos para reproducir
   * el flujo en sync sin depender del state Redux ni del action de
   * react-router. Si `parentMutation` existe, primero se ejecuta esa
   * mutation, su `order.id` se inyecta como `parentOrderId` en
   * `commInput` antes de ejecutar SAVE_COMMUNICATION.
   */
  communicationData?: {
    parentMutation: 'SAVE_GENERIC_ORDER' | 'SAVE_CAMPAIGN' | null;
    parentInput: Record<string, unknown> | null;
    commInput: Record<string, unknown>;
    redirectPath: string;
  };

  /**
   * Para `type: 'action'` creada desde un tramo del RV (básica). El replay
   * vincula tramo ↔ actuación tras el SAVE_ACTION. Puede ser un id sintético
   * `pending-*` si el tramo también se inició sin red: `remapCompoundFromSegment`
   * lo traduce cuando la cola simple reproduce el CreateRouteSegment. Va como
   * campo de la operación, no dentro del snapshot: el snapshot viaja comprimido.
   */
  fromSegmentId?: string | null;

  /**
   * La entidad YA existe en el servidor (la fase 1 se hizo con red y la fase 2
   * no pudo llegar): el replay actualiza por estos ids y NUNCA crea. Sin este
   * campo el replay crea — es lo que duplicaba la actuación cuando la red se
   * cortaba entre las dos fases (spec 2026-09-08-fase2-encolada § 0.2.b).
   */
  existing?: { orderId: string; entityId: string };

  /**
   * `clientOpId` con el que se intentó CREAR con red antes de caer al modo sin
   * red. El replay lo reutiliza: si el create abortado llegó al servidor, la
   * clave de idempotencia devuelve lo ya creado en vez de duplicarlo (D3).
   */
  createClientOpId?: string;

  /**
   * Un intento ya entregó las fotos de la operación a la bandeja (o el guardado
   * en vivo lo hizo antes de encolarla). Los reintentos no las vuelven a
   * entregar: si ya habían subido, la huella no tendría con qué compararlas y
   * se duplicarían (spec 2026-10-01-photo-outbox-identity § 10.4 E3). Las fotos
   * siguen en `localImages`: el reintento ve el mismo formulario.
   */
  photosHandedOff?: boolean;
}

// Mutex lock to prevent race conditions
let queueLock: Promise<void> = Promise.resolve();

async function withLock<T>(action: () => Promise<T>): Promise<T> {
  const nextLock = queueLock.then(() => action()).catch((err) => {
    console.error('Error in offline compound queue lock:', err);
    throw err;
  });
  queueLock = nextLock.then(() => { }).catch(() => { });
  return nextLock;
}

export async function getCompoundQueue(): Promise<OfflineCompoundOperation[]> {
  try {
    const data = await localforage.getItem<OfflineCompoundOperation[]>(COMPOUND_QUEUE_KEY);
    return data || [];
  } catch (error) {
    console.error('Failed to get offline compound queue from localforage', error);
    return [];
  }
}

export async function addCompoundOperation(
  operation: Pick<OfflineCompoundOperation, 'type' | 'parentOrderId' | 'parentOrderRoid' | 'isCampaignParent' | 'sessionSnapshots' | 'localImages'> &
    Partial<Pick<OfflineCompoundOperation, 'communicationData' | 'fromSegmentId' | 'existing' | 'createClientOpId' | 'photosHandedOff'>>
): Promise<void> {
  return withLock(async () => {
    const queue = await getCompoundQueue();

    queue.push({
      ...operation,
      owner: ownerForEnqueue(),
      id: `offline_${operation.type}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
      timestamp: Date.now(),
      retryCount: 0,
      status: 'pending',
    });

    try {
      await localforage.setItem(COMPOUND_QUEUE_KEY, queue);
      window.dispatchEvent(new CustomEvent('offline-queue-changed'));
    } catch (error) {
      console.error('Espacio insuficiente en disco para guardar la operación offline compuesta', error);
    }
  });
}

export async function removeCompoundOperation(id: string): Promise<void> {
  return withLock(async () => {
    const queue = await getCompoundQueue();
    const filtered = queue.filter((op) => op.id !== id);
    await localforage.setItem(COMPOUND_QUEUE_KEY, filtered);
    window.dispatchEvent(new CustomEvent('offline-queue-changed'));
  });
}

export async function updateCompoundStatus(
  id: string,
  status: OfflineCompoundOperation['status'],
  retryCount?: number,
  errorMessage?: string
): Promise<void> {
  return withLock(async () => {
    const queue = await getCompoundQueue();
    const updated = queue.map((op) =>
      op.id === id
        ? {
          ...op,
          status,
          ...(retryCount !== undefined && { retryCount }),
          ...(errorMessage !== undefined && { errorMessage }),
        }
        : op
    );
    await localforage.setItem(COMPOUND_QUEUE_KEY, updated);
    window.dispatchEvent(new CustomEvent('offline-queue-changed'));
  });
}

/** Anota que la operación ya entregó sus fotos a la bandeja (ver `photosHandedOff`). */
export async function markCompoundPhotosHandedOff(id: string): Promise<void> {
  return withLock(async () => {
    const queue = await getCompoundQueue();
    const updated = queue.map((op) => (op.id === id ? { ...op, photosHandedOff: true } : op));
    await localforage.setItem(COMPOUND_QUEUE_KEY, updated);
  });
}

/**
 * Traduce el id sintético de un tramo (`pending-<opId>`) por el real en las
 * operaciones compound que lo referencian. Lo llama la cola simple justo
 * cuando el servidor devuelve el id del tramo; como `handleSync` reproduce la
 * cola simple ANTES que la compound, cuando toque `syncAction` el id ya es real.
 * Devuelve cuántas operaciones cambiaron.
 */
export async function remapCompoundFromSegment(
  syntheticId: string,
  realId: string
): Promise<number> {
  if (!syntheticId || !realId || syntheticId === realId) return 0;
  return withLock(async () => {
    const queue = await getCompoundQueue();
    let changed = 0;
    const updated = queue.map((op) => {
      if (op.fromSegmentId !== syntheticId) return op;
      changed += 1;
      return { ...op, fromSegmentId: realId };
    });
    if (changed > 0) {
      await localforage.setItem(COMPOUND_QUEUE_KEY, updated);
      window.dispatchEvent(new CustomEvent('offline-queue-changed'));
    }
    return changed;
  });
}

export async function getCompoundQueueCount(): Promise<number> {
  const queue = await getCompoundQueue();
  return queue.length;
}

export function hasExceededMaxRetries(operation: OfflineCompoundOperation): boolean {
  return operation.retryCount >= MAX_RETRIES;
}

/**
 * Reactiva las operaciones que agotaron sus intentos (lo pide «Sincronizar
 * ahora» con `resetFailed`). Solo pueden agotarse con respuestas de error del
 * SERVIDOR: los fallos de red ya no cuentan. Devuelve cuántas se reactivaron.
 */
export async function resetExhaustedCompoundOperations(): Promise<number> {
  return withLock(async () => {
    const queue = await getCompoundQueue();
    let reset = 0;
    const updated = queue.map((op) => {
      if (op.retryCount < MAX_RETRIES) return op;
      reset++;
      return { ...op, retryCount: 0, status: 'pending' as const };
    });
    if (reset > 0) {
      await localforage.setItem(COMPOUND_QUEUE_KEY, updated);
      window.dispatchEvent(new CustomEvent('offline-queue-changed'));
    }
    return reset;
  });
}

export async function clearCompoundQueue(): Promise<void> {
  return withLock(async () => {
    await localforage.removeItem(COMPOUND_QUEUE_KEY);
    window.dispatchEvent(new CustomEvent('offline-queue-changed'));
  });
}

/**
 * Sella con `owner` las operaciones que no tienen dueño (las encoladas antes de
 * la #331) y devuelve cuántas selló. Lo llama el logout ANTES de soltar la
 * sesión: la cola ya no se borra al salir (spec 2026-10-12), y una operación sin
 * dueño la enviaría el siguiente operario con su token y a su tenant.
 */
export async function sealUnownedOperations(owner: QueueOwner | undefined): Promise<number> {
  if (!owner) return 0;
  return withLock(async () => {
    const queue = await getCompoundQueue();
    let sealed = 0;
    const updated = queue.map((op): OfflineCompoundOperation => {
      if (op.owner) return op;
      sealed += 1;
      return { ...op, owner };
    });
    if (sealed > 0) await localforage.setItem(COMPOUND_QUEUE_KEY, updated);
    return sealed;
  });
}
